import {
  IControl,
  IContainerControl,
  UIControl,
  MeasureParams,
  DrawParams,
  SizeConstraint,
  PdfSize,
  PdfRect,
} from '../types';
import { PdfPen, PdfBrush, PdfColor } from '../../../pdf/pdf';
import { drawTextClipped } from './primitives';

const DEFAULT_PEN = new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.5);
const WHITE = PdfBrush.White;

class ControlRegistry {
  private controls = new Map<string, IControl>();
  private containers = new Map<string, IContainerControl>();

  register(control: IControl): void {
    if (this.isContainer(control)) {
      this.containers.set(control.type, control);
    } else {
      this.controls.set(control.type, control);
    }
  }

  private isContainer(control: IControl): control is IContainerControl {
    return 'arrange' in control;
  }

  getControl(type: string): IControl | undefined {
    return this.controls.get(type) || this.containers.get(type);
  }

  getContainer(type: string): IContainerControl | undefined {
    return this.containers.get(type);
  }

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const ctrl = this.getControl(control.type);
    if (!ctrl) {
      return this.defaultControl.measure(control, constraint, params);
    }
    return ctrl.measure(control, constraint, params);
  }

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const ctrl = this.getControl(control.type);
    if (!ctrl) {
      this.defaultControl.draw(control, rect, params);
    } else {
      ctrl.draw(control, rect, params);
    }
    if (params.annotationHandler) {
      for (const ann of control.annotations) {
        params.annotationHandler(ann, rect);
      }
    }
  }

  arrangeContainer(
    control: UIControl,
    rect: PdfRect,
    params: DrawParams
  ): { child: UIControl; rect: PdfRect }[] {
    const container = this.getContainer(control.type);
    if (!container) return [];
    return container.arrange(control, rect, params);
  }

  private defaultControl: IControl = {
    type: 'default',
    measure: (control, _constraint, _params) => {
      const explicit = {
        width: parseFloat(control.attrs.Width) || 0,
        height: parseFloat(control.attrs.Height) || 0,
      };
      return new PdfSize(explicit.width || 50, explicit.height || 20);
    },
    draw: (control, rect, params) => {
      params.page.drawRectangle(DEFAULT_PEN, WHITE, rect);
      if (control.content) {
        drawTextClipped(params.page, control.content, params.font, rect);
      }
    },
  };
}

export const registry = new ControlRegistry();

export function measureControl(
  control: UIControl,
  constraint: SizeConstraint,
  params: MeasureParams
): PdfSize {
  return registry.measure(control, constraint, params);
}

export function drawControl(control: UIControl, rect: PdfRect, params: DrawParams): void {
  registry.draw(control, rect, params);
}

export function arrangeContainer(
  control: UIControl,
  rect: PdfRect,
  params: DrawParams
): { child: UIControl; rect: PdfRect }[] {
  return registry.arrangeContainer(control, rect, params);
}

export function isContainer(type: string): boolean {
  return registry.getContainer(type) !== undefined;
}
